/**
 * Order Log — desktop table (ORD-1.2 / ORD-1.3 / ORD-1.4)
 * One renderer for every Order Log surface. The caller owns fetching, filters,
 * paging and export; this component owns the column grid, the cell treatments
 * and the totals footer.
 */
import React from 'react';
import { T, Bone } from '../../reportTokens';
import KpiTooltip from '../../../KpiTooltip';
import type { OrderLogColumnDef, OrderLogRow, OrderLogStatus } from './orderLogModel';
import { rowValue, rowFoots } from './orderLogModel';
import { REFUND_TYPE_LABEL } from '../../refunds/refundModel';

export interface OrderLogTotals {
    /** sum of available values, per column key */
    sums: Record<string, number>;
    /** orders with no data for that column */
    missing: Record<string, number>;
    /** orders in the filtered result set (not the page) */
    orderCount: number;
}

export interface OrderLogTableProps {
    columns: OrderLogColumnDef[];
    rows: OrderLogRow[];
    sortKey: string | null;
    sortDir: 'asc' | 'desc';
    onSort: (col: OrderLogColumnDef) => void;
    onRowClick?: (row: OrderLogRow) => void;
    phoneVisible?: boolean;
    onTogglePhone?: () => void;
    status?: OrderLogStatus;
    /** null hides the footer — shown whenever a financial column is enabled */
    totals?: OrderLogTotals | null;
    fmtMoney: (v: number) => string;
    loading?: boolean;
    skeletonRows?: number;
}

const EM_DASH = '—';

/** "11:53 PM" — the Date column carries the day, so the time cell never repeats it. */
function fmtTime(raw: any): string | null {
    if (!raw) return null;
    const str = String(raw).trim();
    if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(str)) return str.toUpperCase();
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    const h = d.getHours() % 12 || 12;
    return `${String(h).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} ${d.getHours() >= 12 ? 'PM' : 'AM'}`;
}

/** ISO timestamps collapse to their date portion. */
function fmtDate(raw: any): string | null {
    if (!raw) return null;
    const str = String(raw).trim();
    return str.length > 10 && str.indexOf('T') === 10 ? str.slice(0, 10) : str;
}

const headStyle = (active: boolean, align?: string): React.CSSProperties => ({
    fontSize: 10, fontWeight: 600, color: active ? T.accent : T.t3, textTransform: 'uppercase',
    letterSpacing: 0.4, userSelect: 'none', display: 'flex', alignItems: 'center', gap: 3,
    justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
    whiteSpace: 'nowrap',
});

const cellBase: React.CSSProperties = { fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const numCell: React.CSSProperties = { ...cellBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

export function OrderLogTable({
    columns, rows, sortKey, sortDir, onSort, onRowClick,
    phoneVisible = false, onTogglePhone, status = 'paid', totals = null,
    fmtMoney, loading = false, skeletonRows = 5,
}: OrderLogTableProps) {
    const template = columns.map(c => c.width).join(' ');
    const minWidth = Math.max(760, columns.length * 108);

    const SortIcon = ({ col }: { col: OrderLogColumnDef }) => {
        const id = col.sortKey ?? col.key;
        if (sortKey !== id) return <span style={{ color: T.t3, fontSize: 10 }}>↕</span>;
        return <span style={{ color: T.accent, fontSize: 10 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>;
    };

    const maskPhone = (phone: string | null) => {
        if (!phone) return null;
        const digits = String(phone).replace(/\D/g, '');
        const pretty = digits.length === 10
            ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
            : String(phone);
        return phoneVisible ? pretty : `(XXX) XXX-${pretty.slice(-4)}`;
    };

    // ── one cell ────────────────────────────────────────────────────────────
    const renderCell = (row: OrderLogRow, col: OrderLogColumnDef) => {
        if (col.key === 'orderNo') {
            return (
                <div key={col.key} title={row.orderNo} style={{ ...cellBase, fontWeight: 600, color: T.accent, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {row.orderNo}
                    {row.hasRefund && (
                        <span title="Partially refunded" className="material-symbols-rounded" style={{ fontSize: 13, color: T.orange, lineHeight: 1 }}>undo</span>
                    )}
                </div>
            );
        }
        if (col.key === 'customerPhone') {
            const val = maskPhone(row.customerPhone);
            return <div key={col.key} style={{ ...cellBase, color: val ? T.t2 : T.inkLight }}>{val ?? EM_DASH}</div>;
        }
        if (col.key === 'customerName') {
            return <div key={col.key} style={{ ...cellBase, fontWeight: 500, color: T.inkDark }}>{row.customerName ?? 'Walk-in'}</div>;
        }
        if (col.key === 'tableName') {
            // Merged checks collapse to "R4 +1"; the tooltip carries the full list.
            const full = row.tableList.length > 1 ? row.tableList.join(', ') : (row.tableName ?? '');
            return row.tableName
                ? <div key={col.key} title={full} style={{ ...cellBase, color: T.inkDark }}>{row.tableName}</div>
                : <div key={col.key} style={{ ...cellBase, color: T.inkLight }}>{EM_DASH}</div>;
        }
        if (col.key === 'orderTime' || col.key === 'orderDate') {
            const val = col.key === 'orderTime' ? fmtTime(row.orderTime) : fmtDate(row.orderDate);
            return <div key={col.key} style={{ ...cellBase, color: val ? T.t2 : T.inkLight }}>{val ?? EM_DASH}</div>;
        }
        if (col.key === 'guests') {
            return <div key={col.key} style={{ ...cellBase, color: row.guests ? T.t2 : T.inkLight }}>{row.guests ? String(row.guests) : EM_DASH}</div>;
        }
        if (col.key === 'staffName') {
            return row.staffName
                ? <div key={col.key} title={row.staffName} style={{ ...cellBase, color: T.inkDark }}>{row.staffName}</div>
                : <div key={col.key} style={{ ...cellBase, color: T.inkLight }}>{EM_DASH}</div>;
        }
        if (col.key === 'itemPreview' || col.key === 'cancelReason') {
            const val = col.key === 'itemPreview' ? row.itemPreview : row.cancelReason;
            return <div key={col.key} title={val ?? ''} style={{ ...cellBase, color: val ? T.t2 : T.inkLight }}>{val ?? EM_DASH}</div>;
        }
        if (col.key === 'refundType') {
            return (
                <div key={col.key} style={{ ...cellBase, color: row.refundType ? T.t2 : T.inkLight }}>
                    {row.refundType ? REFUND_TYPE_LABEL[row.refundType] : EM_DASH}
                </div>
            );
        }
        if (col.key === 'refunded') {
            const v = row.refunded;
            return (
                <div key={col.key} style={{ ...numCell, color: v && v > 0 ? T.red : T.inkLight, fontWeight: v && v > 0 ? 600 : 400 }}>
                    {v && v > 0 ? fmtMoney(v) : EM_DASH}
                </div>
            );
        }
        if (col.key === 'grandTotal') {
            const v = row.financials.grandTotal;
            const foots = rowFoots(row);
            if (v === null) return <div key={col.key} style={{ ...numCell, color: T.inkLight }}>{EM_DASH}</div>;
            return (
                <div key={col.key} style={{ ...numCell, fontWeight: 600, color: status === 'cancelled' ? T.inkMid : T.inkDark }}>
                    {!foots && (
                        <span className="material-symbols-rounded" title="Components do not foot to the Grand Total — flagged for investigation" style={{ fontSize: 13, color: T.orange, verticalAlign: 'middle', marginRight: 3 }}>warning</span>
                    )}
                    {fmtMoney(v)}
                </div>
            );
        }
        if (col.group === 'financial') {
            const v = rowValue(row, col.key) as number | null;
            if (v === null || v === undefined) return <div key={col.key} style={{ ...numCell, color: T.inkLight }}>{EM_DASH}</div>;
            // A reduction is signed and mid-ink; every additive component is plain dark.
            return col.reduction && v > 0
                ? <div key={col.key} style={{ ...numCell, color: T.inkMid }}>{`−${fmtMoney(v)}`}</div>
                : <div key={col.key} style={{ ...numCell, color: T.inkDark }}>{fmtMoney(v)}</div>;
        }
        const val = rowValue(row, col.key);
        return <div key={col.key} style={{ ...cellBase, color: col.key === 'orderType' ? T.inkDark : T.t2 }}>{val ?? EM_DASH}</div>;
    };

    // ── header ──────────────────────────────────────────────────────────────
    const header = (
        <div style={{ display: 'grid', gridTemplateColumns: template, gap: 8, padding: '8px 0', borderBottom: `1px solid ${T.divider}`, marginBottom: 2 }}>
            {columns.map(col => {
                const id = col.sortKey ?? null;
                const active = !!id && sortKey === id;
                return (
                    <div
                        key={col.key}
                        onClick={() => id && onSort(col)}
                        style={{ ...headStyle(active, col.align), cursor: id ? 'pointer' : 'default' }}
                    >
                        {col.label}
                        {id && <SortIcon col={col} />}
                        {col.key === 'customerPhone' && onTogglePhone && (
                            <span
                                className="material-symbols-rounded"
                                onClick={e => { e.stopPropagation(); onTogglePhone(); }}
                                style={{ fontSize: 13, color: T.t3, cursor: 'pointer', lineHeight: 1 }}
                            >
                                {phoneVisible ? 'visibility' : 'visibility_off'}
                            </span>
                        )}
                        {col.key === 'grandTotal' && status === 'cancelled' && (
                            // Swallow the click so reaching for the tooltip cannot
                            // re-sort the column underneath it.
                            <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'default' }}>
                                <KpiTooltip text="Order value at time of cancellation. Cancelled orders contribute $0 to Net Sales." />
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );

    // ── totals footer ───────────────────────────────────────────────────────
    const footer = totals && (
        <div style={{ position: 'sticky', bottom: 0, background: T.surface, display: 'grid', gridTemplateColumns: template, gap: 8, padding: '10px 0', borderTop: `2px solid ${T.divider}`, marginTop: 2, zIndex: 2 }}>
            {columns.map((col, idx) => {
                if (!col.numeric) {
                    return (
                        <div key={col.key} style={{ ...cellBase, fontSize: 11, fontWeight: 600, color: T.t2 }}>
                            {idx === 0 ? `Total (${totals.orderCount} orders)` : ''}
                        </div>
                    );
                }
                const missing = totals.missing[col.key] || 0;
                const sum = totals.sums[col.key] || 0;
                const text = col.reduction && sum > 0 ? `−${fmtMoney(sum)}` : fmtMoney(sum);
                return (
                    <div
                        key={col.key}
                        title={missing > 0 ? `Excludes ${missing} orders with no data for this field.` : undefined}
                        style={{ ...numCell, fontSize: 11, fontWeight: 700, color: col.reduction ? T.inkMid : T.inkDark, cursor: missing > 0 ? 'help' : 'default' }}
                    >
                        {text}{missing > 0 ? '*' : ''}
                    </div>
                );
            })}
        </div>
    );

    if (loading) {
        return (
            <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth }}>
                    {header}
                    {Array.from({ length: skeletonRows }).map((_, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: template, gap: 8, padding: '10px 0', borderBottom: `1px solid ${T.divider}` }}>
                            {columns.map(c => <Bone key={c.key} w={c.align === 'right' ? 52 : 78} h={12} />)}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth }}>
                {header}
                {rows.map((row, i) => (
                    <div
                        key={row.key}
                        onClick={() => onRowClick && onRowClick(row)}
                        style={{ display: 'grid', gridTemplateColumns: template, gap: 8, padding: '10px 0', borderBottom: i < rows.length - 1 ? `1px solid ${T.divider}` : 'none', cursor: onRowClick ? 'pointer' : 'default', borderRadius: 4, transition: 'background 0.1s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = T.hover)}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                        {columns.map(col => renderCell(row, col))}
                    </div>
                ))}
                {footer}
            </div>
        </div>
    );
}

/** Sum every numeric column across the whole filtered result set. */
export function computeOrderLogTotals(rows: OrderLogRow[], columns: OrderLogColumnDef[]): OrderLogTotals {
    const sums: Record<string, number> = {};
    const missing: Record<string, number> = {};
    columns.filter(c => c.numeric).forEach(col => {
        let sum = 0; let miss = 0;
        rows.forEach(r => {
            const v = rowValue(r, col.key) as number | null;
            if (v === null || v === undefined) miss += 1; else sum += v;
        });
        sums[col.key] = Math.round(sum * 100) / 100;
        missing[col.key] = miss;
    });
    return { sums, missing, orderCount: rows.length };
}
